import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { formatFirstName, formatLastLetters } from '../utils/nameFormat';
import { getDefaultRoute } from '../utils/defaultRoute';
import { identifyAirbridgeUser, trackSignup } from '../utils/airbridge';
import { getPendingInviteId, getPendingHostListingInviteId, getPendingStaffInviteToken, getPendingVenueReferralData } from '../utils/deepLinkHandler';
import { persistAuthSession } from '../utils/authStorage';
import { getPendingPartnerIntent } from '../utils/partnerIntent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// New Google users land here to enter their name before the account is created,
// so chat names follow Allure's privacy formatting (e.g. "Maria Kp.").
export default function GoogleCompleteProfilePage({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const signupToken = state.signupToken || '';

  const [firstName, setFirstName] = useState(formatFirstName(state.suggestedFirstName || ''));
  const [lastName, setLastName] = useState(formatLastLetters(state.suggestedLastName || ''));
  const [loading, setLoading] = useState(false);

  // No signup token means the user arrived here directly — send them to login.
  if (!signupToken) {
    return (
      <div className="app-shell premium-page" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--app-bg)', paddingLeft: 24, paddingRight: 24, paddingBottom: 24 }}>
        <p style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-2)', marginBottom: 16 }}>This sign-up session has expired.</p>
        <button type="button" onClick={() => navigate('/login')} className="auth-primary-btn" style={{ maxWidth: 280 }} data-testid="google-complete-restart-button">
          Back to Login
        </button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName) {
      toast.error('Please enter your first and last name');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/google/complete`, {
        signup_token: signupToken,
        first_name: firstName,
        last_name: lastName,
      });
      await persistAuthSession(res.data.access_token, res.data.user);
      onLogin?.(res.data.user);
      // Airbridge: first-time Google signup → identify + fire signup event,
      // attributing it to any pending invite that drove the flow. Precedence
      // matches RegisterPage: venue_staff > host_to_host > host_to_guest.
      identifyAirbridgeUser(res.data.user);
      const pendingStaffInviteToken = getPendingStaffInviteToken();
      const pendingHostListingInviteId = getPendingHostListingInviteId();
      const pendingInviteId = getPendingInviteId();
      let inviteContext = null;
      if (pendingStaffInviteToken) {
        inviteContext = { inviteId: pendingStaffInviteToken, inviteType: 'venue_staff' };
      } else if (pendingHostListingInviteId) {
        inviteContext = { inviteId: pendingHostListingInviteId, inviteType: 'host_to_host' };
      } else if (pendingInviteId) {
        inviteContext = { inviteId: pendingInviteId, inviteType: 'host_to_guest' };
      }
      trackSignup({
        user: res.data.user,
        method: 'google',
        inviteContext,
      });
      toast.success('Welcome to Allure!');
      // GoogleCompleteProfilePage is only ever reached for BRAND-NEW Google
      // users (existing Google users skip straight to login). To match the
      // email-signup flow's rule — "new users without a deep-link invite
      // must pick a role first" — we only call getDefaultRoute when an
      // invite is pending; otherwise we send them to /select-role.
      const hasPendingDeepLink = Boolean(
        pendingStaffInviteToken || pendingHostListingInviteId || pendingInviteId || getPendingVenueReferralData(),
      );
      if (hasPendingDeepLink) {
        navigate(getDefaultRoute(res.data.user), { replace: true });
      } else {
        // Honour the partner intent set on /select-role so a fresh
        // Google signup lands in the correct onboarding shell (host or
        // venue) instead of the guest picker.
        const partnerIntent = getPendingPartnerIntent();
        if (partnerIntent === 'host') {
          navigate('/host-verify-listing', { replace: true });
        } else if (partnerIntent === 'venue') {
          navigate('/venue/onboarding/1', { replace: true });
        } else {
          // Fresh Google signup with no partner intent — send them to
          // the role picker so they pick Guest / Host / Venue instead
          // of getting silently dropped into the guest app.
          navigate('/select-role', { replace: true });
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not complete sign-up');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={120} height={108} dataTestId="google-complete-logo" style={{ marginTop: 56 }} />
      </div>

      <h1
        className="auth-fade-up"
        style={{ fontFamily: "'Amarante', serif", fontSize: 30, fontWeight: 400, color: 'var(--text)', textAlign: 'center', marginTop: 14, marginBottom: 8, animationDelay: '100ms' }}
      >
        Almost there
      </h1>
      <p
        className="auth-fade-up"
        style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320, lineHeight: 1.5, marginBottom: 28, animationDelay: '120ms' }}
      >
        Tell us how your name should appear in Allure.
      </p>

      <form
        id="google-complete-form"
        onSubmit={handleSubmit}
        className="premium-card auth-fade-up"
        style={{ width: '100%', maxWidth: 360, padding: '28px 24px', animationDelay: '200ms' }}
      >
        <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="gc-first" className="auth-label">First Name</label>
            <input
              id="gc-first"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(formatFirstName(e.target.value))}
              placeholder="Louisa"
              data-testid="google-complete-first-name-input"
              className="auth-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="gc-last" className="auth-label">Last Name</label>
            <input
              id="gc-last"
              type="text"
              autoComplete="family-name"
              value={lastName ? `${lastName}.` : ''}
              onChange={(e) => setLastName(formatLastLetters(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  setLastName((prev) => prev.slice(0, -1));
                }
              }}
              placeholder="K."
              data-testid="google-complete-last-name-input"
              className="auth-input"
            />
          </div>
        </div>
        <p style={{ margin: '0', fontFamily: "'Outfit', sans-serif", fontSize: 12, color: '#999999', lineHeight: 1.4 }}>
          For privacy, we only keep your initials (e.g. {lastName ? `${lastName}.` : 'Kt.'})
        </p>
      </form>

      <motion.button
        type="submit"
        form="google-complete-form"
        disabled={loading}
        whileHover={loading ? {} : { scale: 1.01 }}
        whileTap={loading ? {} : { scale: 0.99 }}
        className="auth-primary-btn auth-fade-up"
        data-testid="google-complete-submit-button"
        style={{ maxWidth: 360, marginTop: 24, animationDelay: '300ms' }}
      >
        {loading ? 'Finishing…' : 'Continue'}
      </motion.button>
    </motion.div>
  );
}
